import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Download, Upload, Pencil, Layers } from "lucide-react";
import { useUiPrefs } from "@/lib/uiPrefs";
import { DensityToggle, tableDensity } from "@/components/ops-ui";

const blank = { combo_mastercode: "", combo_name: "", rrp: "", discount_price: "", items: [], active: true, notes: "" };

export default function ComboCode({ can }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [skuQ, setSkuQ] = useState("");
  const [skuRes, setSkuRes] = useState([]);
  const [imp, setImp] = useState(null); // {file, preview}
  const { density, setDensity } = useUiPrefs("combo-code");

  const load = () => api.get("/combos").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const searchSku = async (q) => {
    setSkuQ(q);
    if (!q.trim()) return setSkuRes([]);
    try { const { data } = await api.get(`/skus?q=${encodeURIComponent(q)}`); setSkuRes(data.slice(0, 8)); }
    catch { setSkuRes([]); }
  };
  const addItem = (s) => {
    setForm(f => {
      const ex = f.items.find(i => i.sku === s.sku);
      const items = ex ? f.items.map(i => i.sku === s.sku ? { ...i, quantity: i.quantity + 1 } : i)
                       : [...f.items, { sku: s.sku, name: s.name, quantity: 1 }];
      return { ...f, items };
    });
    setSkuQ(""); setSkuRes([]);
  };
  const setQty = (sku, q) => setForm(f => ({ ...f, items: f.items.map(i => i.sku === sku ? { ...i, quantity: Math.max(1, parseInt(q || 1, 10)) } : i) }));
  const rmItem = (sku) => setForm(f => ({ ...f, items: f.items.filter(i => i.sku !== sku) }));

  const openNew = () => { setForm(blank); setEditingId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({ ...r, rrp: r.rrp ?? "", discount_price: r.discount_price ?? "" });
    setEditingId(r.id); setOpen(true);
  };
  const save = async () => {
    if (!form.combo_mastercode.trim()) return toast.error("Combo Mastercode required");
    if (!form.combo_name.trim()) return toast.error("Combo Name required");
    if (form.items.length === 0) return toast.error("Add at least one Combo Item");
    const payload = {
      combo_mastercode: form.combo_mastercode.trim(),
      combo_name: form.combo_name.trim(),
      rrp: form.rrp === "" ? null : parseFloat(form.rrp),
      discount_price: form.discount_price === "" ? null : parseFloat(form.discount_price),
      items: form.items.map(i => ({ sku: i.sku, quantity: i.quantity })),
      active: !!form.active, notes: form.notes || "",
    };
    try {
      if (editingId) await api.put(`/combos/${editingId}`, payload);
      else await api.post("/combos", payload);
      toast.success(editingId ? "Combo updated" : "Combo created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this combo?")) return;
    try { await api.delete(`/combos/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const dl = async () => {
    try {
      const res = await api.get("/combos/template.xlsx", { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(res.data); a.download = "combo-template.xlsx"; a.click();
    } catch { toast.error("Download failed"); }
  };
  const pickFile = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/combos/import?commit=false", fd); setImp({ file, preview: data }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const applyImport = async () => {
    const fd = new FormData(); fd.append("file", imp.file);
    try {
      const { data } = await api.post("/combos/import?commit=true", fd);
      if (data.committed) { toast.success(`Imported — ${data.new} new, ${data.updated} updated`); setImp(null); load(); }
      else toast.error("Fix errors before importing");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="combo-code-section">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-slate-500 flex items-center gap-2"><Layers className="w-4 h-4" /> Virtual selling bundles — <b className="text-slate-700">master data only</b> (no stock impact in Phase 1)</div>
        <div className="flex gap-2">
          <DensityToggle density={density} setDensity={setDensity} />
          {can && can("combo:import") && (
            <>
              <Button variant="outline" onClick={dl} data-testid="combo-template-button"><Download className="w-4 h-4 mr-1.5" />Template</Button>
              <label className="inline-flex">
                <input type="file" accept=".xlsx" className="hidden" data-testid="combo-import-input"
                  onChange={e => { pickFile(e.target.files?.[0]); e.target.value = ""; }} />
                <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium border border-slate-300 hover:bg-slate-100 cursor-pointer"><Upload className="w-4 h-4" /> Import Excel</span>
              </label>
            </>
          )}
          {can && can("combo:create") && <Button onClick={openNew} data-testid="combo-new-button" className="bg-slate-900"><Plus className="w-4 h-4 mr-1.5" />New Combo</Button>}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className={`excel-table w-full ${tableDensity(density)}`} data-testid="combo-table">
          <thead><tr><th>Combo Mastercode</th><th>Combo Name</th><th className="text-right">RRP</th><th className="text-right">Discount</th><th>Components</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} data-testid={`combo-row-${r.combo_mastercode}`}>
                <td className="font-mono font-semibold">{r.combo_mastercode}</td>
                <td>{r.combo_name}</td>
                <td className="text-right font-mono">{r.rrp != null ? r.rrp.toFixed(2) : "—"}</td>
                <td className="text-right font-mono">{r.discount_price != null ? r.discount_price.toFixed(2) : "—"}</td>
                <td className="text-xs">{(r.items || []).map(i => `${i.sku} ×${i.quantity}`).join(", ")}</td>
                <td>{r.active ? <span className="text-emerald-600 text-xs font-medium">Active</span> : <span className="text-slate-400 text-xs">Inactive</span>}</td>
                <td className="text-right whitespace-nowrap">
                  {can && can("combo:edit") && <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`combo-edit-${r.combo_mastercode}`}><Pencil className="w-4 h-4" /></Button>}
                  {can && can("combo:delete") && <Button variant="ghost" size="sm" onClick={() => del(r.id)} data-testid={`combo-del-${r.combo_mastercode}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-400">No combos yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="combo-dialog">
          <DialogHeader><DialogTitle>{editingId ? "Edit Combo" : "New Combo"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Combo Mastercode *</Label><Input data-testid="combo-mastercode-input" value={form.combo_mastercode} onChange={e => setForm({ ...form, combo_mastercode: e.target.value })} placeholder="COMBO001" /></div>
              <div><Label>Combo Name *</Label><Input data-testid="combo-name-input" value={form.combo_name} onChange={e => setForm({ ...form, combo_name: e.target.value })} placeholder="Travel Set" /></div>
              <div><Label>RRP</Label><Input type="number" step="0.01" min="0" data-testid="combo-rrp-input" value={form.rrp} onChange={e => setForm({ ...form, rrp: e.target.value })} placeholder="59.90" /></div>
              <div><Label>Discount Price</Label><Input type="number" step="0.01" min="0" data-testid="combo-discount-input" value={form.discount_price} onChange={e => setForm({ ...form, discount_price: e.target.value })} placeholder="49.90" /></div>
            </div>
            <div>
              <Label>Combo Items *</Label>
              <Input value={skuQ} data-testid="combo-item-search" placeholder="Search Master SKU to add…" onChange={e => searchSku(e.target.value)} />
              {skuRes.length > 0 && (
                <div className="border border-slate-200 rounded mt-1 max-h-40 overflow-auto">
                  {skuRes.map(s => (
                    <button type="button" key={s.id} data-testid={`combo-item-result-${s.sku}`} className="block w-full text-left px-2 py-1 text-xs hover:bg-slate-100 font-mono" onClick={() => addItem(s)}>{s.sku} — {s.name}</button>
                  ))}
                </div>
              )}
              <div className="mt-2 space-y-1" data-testid="combo-items-list">
                {form.items.map(i => (
                  <div key={i.sku} className="flex items-center gap-2 text-sm">
                    <span className="font-mono flex-1">{i.sku}</span>
                    <Input type="number" min="1" className="w-20 h-8" data-testid={`combo-item-qty-${i.sku}`} value={i.quantity} onChange={e => setQty(i.sku, e.target.value)} />
                    <button type="button" onClick={() => rmItem(i.sku)} className="text-rose-500 hover:text-rose-700">×</button>
                  </div>
                ))}
                {form.items.length === 0 && <div className="text-xs text-slate-400">No components yet.</div>}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!form.active} onCheckedChange={v => setForm({ ...form, active: !!v })} data-testid="combo-active" /> Active</label>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="combo-save-button" className="bg-slate-900">{editingId ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import preview */}
      <Dialog open={!!imp} onOpenChange={v => !v && setImp(null)}>
        <DialogContent className="max-w-2xl" data-testid="combo-import-dialog">
          <DialogHeader><DialogTitle>Import Preview</DialogTitle></DialogHeader>
          {imp && (
            <div className="space-y-3">
              <div className="flex gap-4 text-sm">
                <span data-testid="combo-import-new">New Combos: <b>{imp.preview.new}</b></span>
                <span data-testid="combo-import-updated">Updated Combos: <b>{imp.preview.updated}</b></span>
                <span data-testid="combo-import-errors" className={imp.preview.errors.length ? "text-rose-600" : ""}>Errors: <b>{imp.preview.errors.length}</b></span>
              </div>
              {imp.preview.errors.length > 0 && (
                <div className="border border-rose-200 rounded max-h-64 overflow-auto">
                  <table className="excel-table w-full text-xs">
                    <thead><tr><th>Row</th><th>Mastercode</th><th>Error</th><th>Suggested correction</th></tr></thead>
                    <tbody>{imp.preview.errors.map((e, k) => (
                      <tr key={k}><td>{e.row}</td><td className="font-mono">{e.mastercode || "—"}</td><td className="text-rose-600">{e.error}</td><td>{e.suggestion}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImp(null)}>Cancel</Button>
            <Button onClick={applyImport} disabled={imp && imp.preview.errors.length > 0} data-testid="combo-import-apply" className="bg-slate-900">Apply Import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
